import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useState, useEffect } from 'react';

// Type pour les articles Substack
interface SubstackPost {
  title: string;
  link: string;
  pubDate: string;
  description: string;
}

export function AboutNewsletterPage() {
  const { t, i18n } = useTranslation('aboutNewsletter');
  const { locale } = useParams();
  const currentLocale = locale || i18n.language || 'fr';
  const [substackPosts, setSubstackPosts] = useState<SubstackPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(true);

  // Récupérer les derniers posts Substack
  useEffect(() => {
    const fetchSubstackPosts = async () => {
      try {
        const response = await fetch('https://corentinleroux.substack.com/feed');
        const xmlText = await response.text();

        // Parser le XML RSS
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
        const items = xmlDoc.querySelectorAll('item');

        const posts: SubstackPost[] = Array.from(items).slice(0, 3).map(item => {
          const title = item.querySelector('title')?.textContent || '';
          const link = item.querySelector('link')?.textContent || '';
          const pubDate = item.querySelector('pubDate')?.textContent || '';
          const description = item.querySelector('description')?.textContent || '';

          // Nettoyer la description (enlever le HTML)
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = description;
          const cleanDescription = tempDiv.textContent || tempDiv.innerText || '';

          return { title, link, pubDate, description: cleanDescription.slice(0, 200) + '...' };
        });

        setSubstackPosts(posts);
      } catch (error) {
        console.error('Erreur lors du chargement des posts Substack:', error);
      } finally {
        setLoadingPosts(false);
      }
    };

    fetchSubstackPosts();
  }, []);

  // SVG Icons personnalisés
  const MailIcon = () => (
    <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect x="15" y="25" width="70" height="50" rx="5" fill="white" opacity="0.3"/>
      <path d="M 15 25 L 50 55 L 85 25" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="15" y="25" width="70" height="50" rx="5" fill="none" stroke="white" strokeWidth="3"/>
      <circle cx="65" cy="40" r="8" fill="white" opacity="0.5"/>
    </svg>
  );

  const CalendarIcon = () => (
    <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect x="20" y="25" width="60" height="55" rx="5" fill="white" opacity="0.3"/>
      <rect x="20" y="35" width="60" height="45" rx="3" fill="white" opacity="0.5"/>
      <line x1="35" y1="20" x2="35" y2="32" stroke="white" strokeWidth="4" strokeLinecap="round"/>
      <line x1="65" y1="20" x2="65" y2="32" stroke="white" strokeWidth="4" strokeLinecap="round"/>
      <line x1="25" y1="42" x2="75" y2="42" stroke="white" strokeWidth="2" opacity="0.6"/>
      <circle cx="35" cy="55" r="3" fill="white"/>
      <circle cx="50" cy="55" r="3" fill="white"/>
      <circle cx="65" cy="55" r="3" fill="white"/>
      <circle cx="35" cy="68" r="3" fill="white"/>
      <circle cx="50" cy="68" r="3" fill="white"/>
    </svg>
  );

  const TargetIcon = () => (
    <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="35" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.3"/>
      <circle cx="50" cy="50" r="25" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.5"/>
      <circle cx="50" cy="50" r="15" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.7"/>
      <circle cx="50" cy="50" r="6" fill="currentColor"/>
      <path d="M 70 30 L 85 15 M 82 18 L 88 12 M 82 12 L 88 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );

  const NewsletterIcon = () => (
    <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect x="20" y="20" width="60" height="60" rx="4" fill="currentColor" opacity="0.1"/>
      <rect x="20" y="20" width="60" height="60" rx="4" fill="none" stroke="currentColor" strokeWidth="2.5"/>
      <line x1="30" y1="35" x2="70" y2="35" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
      <line x1="30" y1="45" x2="60" y2="45" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.7"/>
      <line x1="30" y1="55" x2="65" y2="55" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.7"/>
      <rect x="30" y="63" width="15" height="10" rx="2" fill="currentColor" opacity="0.3"/>
      <rect x="48" y="63" width="15" height="10" rx="2" fill="currentColor" opacity="0.3"/>
    </svg>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F06C6E]/5 via-white to-aspexit-green/5">
      <SEOHead
        title={t('meta.title', 'Newsletter - Aspexit')}
        description={t('meta.description', 'La newsletter agriculture numérique par Corentin Leroux')}
        path="/about-newsletter"
      />
      <div className="container mx-auto px-4 py-12 max-w-4xl">

        {/* Back button */}
        <Button
          asChild
          variant="ghost"
          className="mb-6 -ml-4"
        >
          <Link to={`/${currentLocale}`}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('backToHome') || 'Retour à l\'accueil'}
          </Link>
        </Button>

        {/* Header avec dégradé */}
        <div className="bg-gradient-to-r from-[#F06C6E] to-[#E85658] rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <MailIcon />
            </div>
            <div>
              <h1 className="text-4xl font-heading font-extrabold">
                {t('title') || 'La Newsletter Aspexit'}
              </h1>
              <p className="text-white/90 text-lg mt-2">
                {t('subtitle') || 'Décryptages et analyses du numérique agricole'}
              </p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <Card className="p-8 mb-8 border-2 border-gray-100 shadow-lg">
          <h2 className="text-2xl font-heading font-bold text-aspexit-black mb-4 flex items-center gap-2">
            <div className="w-6 h-6 text-[#F06C6E]">
              <TargetIcon />
            </div>
            {t('what.title') || 'Qu\'est-ce que la newsletter Aspexit ?'}
          </h2>
          <p className="text-aspexit-gray leading-relaxed mb-4">
            {t('what.description1') || 'La newsletter Aspexit, rédigée par Corentin Leroux, vous propose des analyses indépendantes et approfondies sur l\'écosystème numérique agricole.'}
          </p>
          <p className="text-aspexit-gray leading-relaxed">
            {t('what.description2') || 'Chaque édition décrypte les tendances, les technologies émergentes et les enjeux stratégiques qui façonnent l\'agriculture de demain.'}
          </p>
        </Card>

        {/* Fréquence */}
        <Card className="p-8 mb-8 bg-gradient-to-br from-aspexit-green/5 to-white border-2 border-aspexit-green/20 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-aspexit-green rounded-lg flex items-center justify-center flex-shrink-0">
              <CalendarIcon />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold text-aspexit-black mb-3">
                {t('frequency.title') || 'Fréquence d\'envoi'}
              </h2>
              <p className="text-lg text-aspexit-black font-semibold mb-2">
                {t('frequency.rate') || '1 à 2 fois par mois'}
              </p>
              <p className="text-aspexit-gray leading-relaxed">
                {t('frequency.description') || 'Une newsletter légère et qualitative, sans spam. Vous recevez uniquement du contenu à forte valeur ajoutée, au rythme qui vous convient.'}
              </p>
            </div>
          </div>
        </Card>

        {/* Dernières publications Substack */}
        <Card className="p-8 mb-8 border-2 border-gray-100 shadow-lg">
          <h2 className="text-2xl font-heading font-bold text-aspexit-black mb-6 flex items-center gap-2">
            <div className="w-6 h-6 text-[#F06C6E]">
              <NewsletterIcon />
            </div>
            {t('content.title') || 'Dernières publications'}
          </h2>

          {loadingPosts ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#F06C6E]"></div>
              <p className="text-aspexit-gray mt-4">Chargement des dernières publications...</p>
            </div>
          ) : substackPosts.length > 0 ? (
            <div className="space-y-6">
              {substackPosts.map((post, index) => (
                <a
                  key={index}
                  href={post.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-6 bg-gradient-to-br from-[#F06C6E]/5 to-white border-2 border-gray-100 rounded-xl hover:border-[#F06C6E]/40 hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <h3 className="font-semibold text-aspexit-black text-lg group-hover:text-[#F06C6E] transition-colors">
                      {post.title}
                    </h3>
                    <svg
                      className="w-5 h-5 text-[#F06C6E] flex-shrink-0 group-hover:translate-x-1 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                  <p className="text-aspexit-gray text-sm mb-3 line-clamp-3">
                    {post.description}
                  </p>
                  <p className="text-xs text-aspexit-gray/70">
                    {new Date(post.pubDate).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </a>
              ))}

              <div className="pt-4 text-center">
                <a
                  href="https://corentinleroux.substack.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#F06C6E] hover:text-[#E85658] font-semibold transition-colors"
                >
                  Voir toutes les publications
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-aspexit-gray mb-4">Aucune publication disponible pour le moment.</p>
              <a
                href="https://corentinleroux.substack.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#F06C6E] hover:text-[#E85658] font-semibold transition-colors"
              >
                Visiter la page Substack →
              </a>
            </div>
          )}
        </Card>

        {/* CTA S'abonner */}
        <Card className="p-8 bg-gradient-to-r from-[#F06C6E] to-[#E85658] text-white text-center shadow-xl">
          <h2 className="text-2xl font-heading font-bold mb-3">
            {t('cta.title') || 'Prêt à recevoir les analyses ?'}
          </h2>
          <p className="text-white/90 mb-6">
            {t('cta.description') || 'Rejoignez les professionnels qui suivent déjà l\'actualité du numérique agricole'}
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white text-aspexit-black hover:bg-gray-100 font-semibold shadow-lg"
          >
            <Link to={`/${currentLocale}#newsletter`} className="inline-flex items-center gap-2">
              <svg viewBox="0 0 100 100" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                <rect x="15" y="30" width="70" height="45" rx="4" fill="currentColor" opacity="0.2"/>
                <path d="M 15 30 L 50 55 L 85 30" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="15" y="30" width="70" height="45" rx="4" fill="none" stroke="currentColor" strokeWidth="3.5"/>
              </svg>
              {t('cta.button') || 'S\'abonner à la newsletter'}
            </Link>
          </Button>
          <p className="text-sm text-white/80 mt-4">
            {t('cta.disclaimer') || 'Gratuit • Sans spam • Désinscription en 1 clic'}
          </p>
        </Card>

      </div>
    </div>
  );
}
